// Utility to format legal content text into HTML with proper structure
export function formatLegalContent(content: string, type: 'privacy' | 'terms' | 'disclaimer'): string {
  let html = content;

  // Replace email addresses with clickable links
  html = html.replace(
    /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
    '<a href="mailto:$1">$1</a>'
  );

  // Replace website URLs with clickable links
  html = html.replace(
    /(www\.[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
    '<a href="https://$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  // Add internal links
  if (type === 'terms') {
    html = html.replace(
      /Privacy Policy(?! \()/g,
      '<a href="/legal?section=privacy">Privacy Policy</a>'
    );
  } else if (type === 'privacy') {
    html = html.replace(
      /Terms of Service(?! \()/g,
      '<a href="/legal?section=terms">Terms of Service</a>'
    );
  }

  // Split into lines for processing
  const lines = html.split('\n');
  const formattedLines: string[] = [];
  let inList = false;
  let currentParagraph: string[] = [];

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const text = currentParagraph.join(' ').trim();
      if (text) {
        formattedLines.push(`<p>${text}</p>`);
      }
      currentParagraph = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Skip empty lines unless we're flushing content
    if (!line) {
      flushParagraph();
      if (inList) {
        formattedLines.push('</ul>');
        inList = false;
      }
      continue;
    }

    // Skip "Last Updated" line as it's already handled separately
    if (line.startsWith('Last Updated:')) {
      continue;
    }

    // Check if it's a numbered section (like "1. Company Information")
    if (/^\d+\.\s/.test(line)) {
      flushParagraph();
      if (inList) {
        formattedLines.push('</ul>');
        inList = false;
      }
      formattedLines.push(`<h2>${line}</h2>`);
      continue;
    }

    // Check if it's a main heading (all caps or title case without number)
    if (
      line === line.toUpperCase() &&
      line.length > 3 &&
      !line.startsWith('•') &&
      !line.match(/^[A-Z][a-z]/)
    ) {
      flushParagraph();
      if (inList) {
        formattedLines.push('</ul>');
        inList = false;
      }
      formattedLines.push(`<h2>${line}</h2>`);
      continue;
    }

    // Check if it's a subsection heading (title case, not a bullet, not all caps)
    if (
      /^[A-Z][a-zA-Z\s]+$/.test(line) &&
      line.length < 80 &&
      !line.startsWith('•') &&
      line !== line.toUpperCase() &&
      !line.includes('(') &&
      !line.includes(',')
    ) {
      flushParagraph();
      if (inList) {
        formattedLines.push('</ul>');
        inList = false;
      }
      formattedLines.push(`<h3>${line}</h3>`);
      continue;
    }

    // Check if it's a bullet point
    if (line.startsWith('•')) {
      flushParagraph();
      if (!inList) {
        formattedLines.push('<ul>');
        inList = true;
      }
      const bulletText = line.substring(1).trim();
      formattedLines.push(`<li>${bulletText}</li>`);
      continue;
    }

    // Regular text - add to current paragraph
    currentParagraph.push(line);
  }

  // Flush any remaining content
  flushParagraph();
  if (inList) {
    formattedLines.push('</ul>');
  }

  return formattedLines.join('\n');
}
